import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Linkedin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const EMAIL_VERIFY_URL = "https://crmdev.miti.us/api/v1/email/verify-email";
const DEMO_REQUEST_URL = "https://crmdev.miti.us/api/request-demo";

const naicsCodes = [
  "513210  541511  541512",
  "541513  541519  518210",
  "541618  541690  541611",
  "561110  561210  541715",
  "511210  541612  541613",
  "611420",
];

const productLinks = [
  { name: "Signulu", logo: "/assets/signuluone-lockup.png", href: "https://signulu.signuluone.com/" },
  { name: "DMS", logo: "/assets/logo-dms.webp", href: "https://dms.signuluone.com/" },
  { name: "QuikHyr AI", logo: "/assets/logo-quikhyr.svg", href: "https://quikhyr.ai/" },
  { name: "Attainica", logo: "/assets/logo-attainica.png", href: "https://www.attainica.com/" },
];

const ColumnHeading = ({ children }) => (
  <>
    <h3 className="font-heading font-semibold text-base text-slate-900">{children}</h3>
    <div className="mt-2 h-0.5 w-8 bg-miti-blue" />
  </>
);

const Footer = () => {
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | verifying | valid | invalid
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const updateEmail = (e) => {
    setEmail(e.target.value);
    setEmailStatus("idle");
    setEmailError("");
  };

  const verifyEmail = async (value) => {
    if (!value) return;
    setEmailStatus("verifying");
    setEmailError("");
    try {
      const res = await fetch(EMAIL_VERIFY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value }),
      });
      const data = await res.json();
      const result = data?.data?.results?.[0];
      if (result?.is_valid === true) {
        setEmailStatus("valid");
      } else {
        setEmailStatus("invalid");
        setEmailError("Please enter a valid email address.");
      }
    } catch {
      setEmailStatus("invalid");
      setEmailError("Could not verify email. Please try again.");
    }
  };

  useEffect(() => {
    const value = email.trim();
    if (!/\S+@\S+\.\S+/.test(value)) {
      setEmailStatus("idle");
      return;
    }
    const timer = setTimeout(() => verifyEmail(value), 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const subscribe = async (e) => {
    e.preventDefault();
    if (emailStatus !== "valid") return;
    setSubmitting(true);
    try {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const requestDatetime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

      const res = await fetch(DEMO_REQUEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "miti.us",
          subject: "Subscribe to Newsletter",
          request_datetime: requestDatetime,
          name: "MITIUS",
          email,
        }),
      });
      const data = await res.json();
      if (data?.success) {
        toast.success("Subscribed! You'll receive the latest insights and updates.");
        setEmail("");
        setEmailStatus("idle");
      } else {
        toast.error(data?.message || "Failed to subscribe. Please try again.");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer id="contact" data-testid="footer" className="bg-white">
      <div className="relative overflow-hidden bg-miti-navy">
        <img src="/assets/newsletter-bg.png" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-miti-navy/55" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-white">
              Sign up for Newsletter
            </h2>
            <p className="mt-2 text-sm md:text-base text-slate-300">
              Subscribe to Our Newsletter for the Latest Insights and Updates
            </p>
          </motion.div>
          <form onSubmit={subscribe} className="flex w-full max-w-xl gap-3 items-start" data-testid="newsletter-form">
            <div className="flex-1 flex flex-col gap-1.5">
              <input
                type="email"
                value={email}
                onChange={updateEmail}
                placeholder="Email address"
                data-testid="newsletter-email-input"
                className={`w-full rounded-md bg-white px-5 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-shadow ${
                  emailStatus === "invalid"
                    ? "ring-2 ring-red-400"
                    : emailStatus === "valid"
                    ? "ring-2 ring-emerald-400"
                    : "focus:ring-sky-400"
                }`}
              />
              {emailStatus === "verifying" && (
                <p className="flex items-center gap-1 text-xs text-slate-300">
                  <Loader2 size={12} className="animate-spin" /> Verifying…
                </p>
              )}
              {emailStatus === "invalid" && <p className="text-xs text-red-400">{emailError}</p>}
              {emailStatus === "valid" && (
                <p className="flex items-center gap-1 text-xs text-emerald-400">
                  <CheckCircle2 size={12} /> Email is valid
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={emailStatus !== "valid" || submitting}
              data-testid="newsletter-subscribe-button"
              className="shrink-0 rounded-md bg-white px-7 py-3.5 text-xs font-bold tracking-[0.15em] text-miti-navy hover:bg-sky-300 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : "SUBSCRIBE"}
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Logo />
            <p className="mt-5 text-sm leading-relaxed text-slate-500">
              <span className="text-slate-900 font-semibold">Millennium Info Tech</span> crafts AI-driven
              applications and solutions, seamlessly integrating cloud technology, data, and automation. As a
              Microsoft Gold Partner, we harness cutting-edge tools and expertise to drive impactful results.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-5">
              {productLinks.map((p) => (
                <a
                  key={p.name}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-product-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="hover:opacity-70 transition-opacity"
                >
                  <img
                    src={p.logo}
                    alt={p.name}
                    className={`${p.name === "DMS" ? "h-6" : "h-8"} w-auto object-contain`}
                  />
                </a>
              ))}
            </div>
          </div>

          <div>
            <ColumnHeading>NAICS Codes</ColumnHeading>
            <div className="mt-5 space-y-2.5 text-sm text-slate-700 font-medium tracking-wide">
              {naicsCodes.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          </div>

          <div>
            <ColumnHeading>Company</ColumnHeading>
            <p className="mt-5 text-sm font-semibold text-slate-900">Millennium Info Tech</p>
            <div className="mt-4 space-y-2.5 text-sm text-slate-500">
              <p><span className="text-slate-800 font-semibold">CAGE CODE</span> 5PJN1</p>
              <p><span className="text-slate-800 font-semibold">UEI</span> DMSZL3N1JTH1</p>
              <p><span className="text-slate-800 font-semibold">DUNS</span> 021045872</p>
              <p><span className="text-slate-800 font-semibold">DHS E-VERIFY</span> 201184</p>
              <p><span className="text-slate-800 font-semibold">MPN</span> 6257599</p>
            </div>
          </div>

          <div>
            <ColumnHeading>Get in Touch</ColumnHeading>
            <div className="mt-5 space-y-4 text-sm text-slate-500">
              <p className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0 text-miti-blue" />
                101 Morgan Lane, Suite # 188, 
                Plainsboro NJ 08536.
              </p>
              <a href="mailto:info@miti.us" data-testid="footer-email-link" className="flex items-center gap-3 hover:text-miti-blue transition-colors">
                <Mail size={16} className="shrink-0 text-miti-blue" />
                info@miti.us
              </a>
              <a href="tel:+16097507120" data-testid="footer-phone-link" className="flex items-center gap-3 hover:text-miti-blue transition-colors">
                <Phone size={16} className="shrink-0 text-miti-blue" />
                (+1) 609-750-7120
              </a>
              <a
                href="https://www.linkedin.com/company/millenniuminfotechusa"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-miti-blue transition-colors"
              >
                Follow us on
                <Linkedin size={16} className="text-miti-blue" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 py-6 text-center">
        <p className="text-sm text-slate-400">© 2026 Millennium Infotech, All rights reserved.</p>
      </div>
    </footer>
  );
};

export default Footer;
